import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ClubCrest } from '@/components/ui-kit/ClubCrest'
import { GhostButton, OddsChip, PrimaryButton } from '@/components/ui-kit/Buttons'
import { ProgressBar } from '@/components/ui-kit/Primitives'
import { ScreenShell } from '@/components/ui-kit/Shell'
import { decidedCount, profit, remainingBudget } from '@/lib/game'
import { de } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { BetPick, MatchOutcome, PhoneMatch } from '@/types/game'

const OUTCOMES: MatchOutcome[] = ['home', 'draw', 'away']

interface BetScreenProps {
    matches: PhoneMatch[]
    onPick: (position: number, pick: BetPick) => void
    onSubmit: () => void
    onHelp?: () => void
}

/**
 * Tippabgabe, ein Spiel nach dem anderen.
 *
 * Das Budget zählt sichtbar herunter - 10 € je platzierter Wette. Ein
 * übersprungenes Spiel kostet nichts, und genau das soll auffallen.
 */
export function BetScreen({ matches, onPick, onSubmit, onHelp }: BetScreenProps) {
    const { t } = useTranslation()

    // Beim Neuladen dort weitermachen, wo die Schuelerin war: beim ersten
    // Spiel ohne Tipp. Ohne das landete sie wieder bei Spiel 1 und musste sich
    // durch alles durchklicken, was sie laengst getippt hatte - die Tipps sind
    // zwar da, aber der Weg dorthin nervt bei zehn Spielen.
    const [index, setIndex] = useState(() => {
        const offen = matches.findIndex((entry) => entry.pick === null)

        return offen === -1 ? 0 : offen
    })

    const match = matches[index]
    const last = index === matches.length - 1
    const budget = remainingBudget(matches)
    const decided = decidedCount(matches)
    const open = matches.length - decided

    if (!match) {
        return null
    }

    // Die Wahl bleibt stehen, bis die Schuelerin selbst weitertippt. Das
    // automatische Weiterblaettern ist bewusst weggefallen: Man soll sehen, was
    // man gewaehlt hat, es noch aendern koennen und den naechsten Schritt
    // selbst gehen - so wie im Clickdummy.
    const choose = (pick: BetPick) => onPick(match.position, pick)

    return (
        <ScreenShell>
            {/* Jeder Screen braucht eine Überschrift - hier steht sie nur für
                Screenreader, weil die Kopfzeile die Information schon zeigt. */}
            <h1 className="sr-only">{t('bet.title')}</h1>

            <div className="flex items-center gap-2.5">
                <button
                    type="button"
                    onClick={() => setIndex(Math.max(0, index - 1))}
                    disabled={index === 0}
                    aria-label={t('bet.prev')}
                    className="flex size-8 flex-none items-center justify-center rounded-[10px] border border-wb-border bg-wb-panel-hi text-lg disabled:opacity-30"
                >
                    ‹
                </button>
                {onHelp && (
                    <button
                        type="button"
                        onClick={onHelp}
                        aria-label={t('common.help')}
                        className="flex size-8 flex-none items-center justify-center rounded-[10px] border border-wb-border bg-wb-panel-hi text-sm font-bold"
                    >
                        ?
                    </button>
                )}

                <div className="flex-1">
                    <div className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('bet.match')}
                    </div>
                    <div className="text-[15px] font-extrabold" data-testid="match-counter">
                        {index + 1} / {matches.length}
                    </div>
                </div>

                <div className="text-right">
                    <div className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('bet.budget')}
                    </div>
                    <div
                        className="wb-num text-[15px] font-extrabold text-wb-accent"
                        data-testid="budget"
                    >
                        {budget} €
                    </div>
                </div>
            </div>

            {/* Der Balken zeigt die Position, nicht die Zahl der Tipps: Er
                steht direkt unter „Spiel 10 / 10" und wurde deshalb als
                dieselbe Angabe gelesen - stand dort 10/10 und der Balken bei
                90 %, sah das nach einem Fehler aus. Ob alle Spiele entschieden
                sind, sagt der Knopf am Fuß des Screens. Die Auflösung zaehlt
                genauso. */}
            <ProgressBar value={index + 1} total={matches.length} className="mt-3" />

            <div className="mt-3 wb-scroll flex flex-1 flex-col gap-3.5 overflow-y-auto rounded-2xl border border-wb-border bg-linear-to-b from-wb-panel to-wb-panel-lo p-4">
                <div className="text-right text-[11px] font-bold tracking-[.05em] text-wb-dim">
                    {t('bet.stake')}
                </div>

                <div className="flex items-center gap-2.5">
                    <div className="flex flex-1 flex-col items-center gap-1.5">
                        <span className="text-[9px] font-extrabold tracking-[.12em] text-wb-dim uppercase">
                            {t('bet.home')}
                        </span>
                        <ClubCrest club={match.home} size={54} />
                        <span className="text-center text-[13px] font-bold text-balance">
                            {match.home.name}
                        </span>
                    </div>

                    <span className="rounded-full bg-wb-panel-hi px-2.5 py-1.5 text-xs font-extrabold tracking-[.1em] text-wb-dim">
                        {t('bet.versus')}
                    </span>

                    <div className="flex flex-1 flex-col items-center gap-1.5">
                        <span className="text-[9px] font-extrabold tracking-[.12em] text-wb-dim uppercase">
                            {t('bet.away')}
                        </span>
                        <ClubCrest club={match.away} size={54} />
                        <span className="text-center text-[13px] font-bold text-balance">
                            {match.away.name}
                        </span>
                    </div>
                </div>

                <div className="flex gap-2">
                    {OUTCOMES.map((outcome) => (
                        <OddsChip
                            key={outcome}
                            label={t(`bet.odds.${outcome}`)}
                            value={match.odds[outcome]}
                            selected={match.pick === outcome}
                            onClick={() => choose(outcome)}
                        />
                    ))}
                </div>

                {match.pick !== null && match.pick !== 'skip' && (
                    <div
                        className="flex animate-wb-rise items-center justify-between rounded-[10px] bg-wb-panel-hi px-3 py-2.5"
                        data-testid="possible-win"
                    >
                        <span className="text-xs font-semibold text-wb-dim">
                            {t('bet.possibleWin')}
                        </span>
                        <span className="wb-num text-lg font-extrabold text-wb-win">
                            +{de(profit(match.odds[match.pick]))} €
                        </span>
                    </div>
                )}

                <div className="flex items-center gap-2.5 text-[10px] font-bold tracking-[.12em] text-wb-muted">
                    <span className="h-px flex-1 bg-wb-border" />
                    {t('bet.or')}
                    <span className="h-px flex-1 bg-wb-border" />
                </div>

                <GhostButton
                    onClick={() => choose('skip')}
                    className={cn(
                        match.pick === 'skip' && 'border-wb-win bg-wb-win/10 text-wb-win',
                    )}
                    aria-pressed={match.pick === 'skip'}
                >
                    {match.pick === 'skip' ? t('bet.skipped') : t('bet.skip')}
                </GhostButton>
                <p className="-mt-1 text-center text-[11px] text-wb-muted">{t('bet.skipHint')}</p>
            </div>

            <div className="mt-3 flex flex-col gap-2">
                {/* Weiter erst nach einer Entscheidung - auch "keine Wette
                    abgeben" ist eine. Sonst liesse sich ein Spiel unbemerkt ohne
                    Tipp hinter sich lassen. So auch im Clickdummy. */}
                {!last && (
                    <PrimaryButton
                        onClick={() => setIndex(Math.min(matches.length - 1, index + 1))}
                        disabled={match.pick === null}
                    >
                        {t('bet.next')}
                    </PrimaryButton>
                )}

                {/* WETTBANDIT-67: Beim letzten Spiel gab es keinen Knopf zum
                    Abgeben, solange noch ein Spiel offen war - der Tippschein
                    liess sich dann gar nicht abschicken. Jetzt steht er hier
                    immer, und die Abgabe laeuft ueber eine Rueckfrage. */}
                {last && (
                    <PrimaryButton onClick={onSubmit} disabled={match.pick === null}>
                        {t('bet.submit')}
                    </PrimaryButton>
                )}

                {open > 0 && (
                    <p className="text-center text-[11px] text-wb-muted">
                        {t('bet.openMatches', { count: open })}
                    </p>
                )}
            </div>
        </ScreenShell>
    )
}
